/**
 * FamilyMemberProfileCard Component
 *
 * Enhanced card showing full profile info: avatar, name, DOB, calendar count.
 * Tappable to navigate to edit page.
 */

import { Pressable, StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Animated, { useSharedValue, useAnimatedStyle, withTiming } from 'react-native-reanimated';
import { Colors, Shadows } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import type { FamilyMember } from '@/types/family-member';
import { FamilyMemberAvatar } from './FamilyMemberAvatar';
import { getContrastTextColor } from '@/lib/utils/color-contrast';

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

type FamilyMemberProfileCardProps = {
  member: FamilyMember;
  calendarCount: number;
  onPress: (member: FamilyMember) => void;
};

export function FamilyMemberProfileCard({
  member,
  calendarCount,
  onPress,
}: FamilyMemberProfileCardProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  const scale = useSharedValue(1);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }));

  const handlePressIn = () => {
    scale.value = withTiming(0.97, { duration: 100 });
  };

  const handlePressOut = () => {
    scale.value = withTiming(1, { duration: 100 });
  };

  const formatDateOfBirth = (dateString: string | null): string | null => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return null;
    }
  };

  const formattedDob = formatDateOfBirth(member.dateOfBirth);
  const calendarLabel = calendarCount === 1 ? '1 calendar' : `${calendarCount} calendars`;

  return (
    <AnimatedPressable
      style={[
        styles.container,
        Shadows.sm,
        { backgroundColor: palette.cardBackground },
        animatedStyle,
      ]}
      onPress={() => onPress(member)}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
    >
      <FamilyMemberAvatar
        name={member.name}
        color={member.color}
        size="medium"
        imageUri={member.profilePicture}
      />

      <View style={styles.content}>
        <View style={styles.nameRow}>
          <Text
            style={[styles.name, { color: palette.text, fontFamily: getBodyFont() }]}
            numberOfLines={1}
          >
            {member.name}
          </Text>
          {member.isMe && (
            <View style={[styles.badge, { backgroundColor: palette.primary }]}>
              <Text style={[styles.badgeText, { color: getContrastTextColor(palette.primary), fontFamily: getBodyFont() }]}>You</Text>
            </View>
          )}
        </View>

        <View style={styles.details}>
          {formattedDob && (
            <Text
              style={[styles.detailText, { color: palette.textMuted, fontFamily: getBodyFont() }]}
              numberOfLines={1}
            >
              {formattedDob}
            </Text>
          )}
          <Text
            style={[styles.detailText, { color: palette.textMuted, fontFamily: getBodyFont() }]}
            numberOfLines={1}
          >
            {calendarLabel}
          </Text>
        </View>
      </View>

      <Ionicons name="chevron-forward" size={20} color={palette.textMuted} />
    </AnimatedPressable>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.lg,
    borderRadius: BorderRadius.md,
    marginBottom: Spacing.md,
    gap: Spacing.md + 2, // 14px
  },
  content: {
    flex: 1,
    gap: Spacing.xs,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
  },
  name: {
    fontSize: 17,
    fontWeight: '600',
  },
  badge: {
    paddingHorizontal: Spacing.sm,
    paddingVertical: Spacing.xxs,
    borderRadius: BorderRadius.sm + 2, // 10px
  },
  badgeText: {
    fontSize: 11,
    fontWeight: '500',
  },
  details: {
    gap: Spacing.xxs,
  },
  detailText: {
    fontSize: 14,
  },
});
